import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { HOME, IMAGES } from "../data/mock";
import Reveal from "../components/Reveal";
import Marquee from "../components/Marquee";
import ClientsSection from "../components/ClientsSection";
import { NumberedRow, SectionHead, CTABand } from "../components/SharedUI";

const Home = () => {
  const d = HOME;
  return (
    <div>
      {/* HERO */}
      <section className="grain bg-[#16233f] text-white pt-32 md:pt-40 pb-0">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center min-h-[70vh] pb-24">
            {/* left */}
            <div>
              <Reveal>
                <div className="eyebrow mb-7">{d.eyebrow}</div>
              </Reveal>
              <Reveal delay={80}>
                <h1 className="font-serif font-semibold leading-[1.0] text-[54px] md:text-[80px]">
                  {d.titleLines.map((l, i) => (
                    <span key={i} className="block">
                      {l.plain}
                      <span className="italic text-[var(--gold)]">{l.accent}</span>
                    </span>
                  ))}
                </h1>
              </Reveal>
              <Reveal delay={160}>
                <p className="text-white/65 text-lg leading-relaxed max-w-xl mt-8">
                  {d.description}
                </p>
              </Reveal>
              <Reveal delay={240}>
                <div className="flex flex-wrap gap-4 mt-10">
                  <Link to={d.primaryCta.path} className="btn-gold inline-flex items-center gap-2 px-8 py-4 rounded-sm">
                    {d.primaryCta.label} <ArrowRight size={18} />
                  </Link>
                  <Link to={d.secondaryCta.path} className="btn-outline inline-flex items-center gap-2 px-8 py-4 rounded-sm">
                    {d.secondaryCta.label} <ArrowUpRight size={18} />
                  </Link>
                </div>
              </Reveal>
            </div>

            {/* right image */}
            <Reveal delay={200}>
              <div className="relative max-w-[520px] ml-auto">
                <div className="absolute -inset-4 border border-[var(--gold)]/30 rounded-sm translate-x-4 -translate-y-4" />
                <div className="img-zoom relative rounded-sm overflow-hidden shadow-2xl">
                  <img src={IMAGES.hero} alt="A professional handshake" className="w-full h-[460px] object-cover" />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-[#0d1526] border border-white/10 px-7 py-5 rounded-sm shadow-xl">
                  <div className="font-serif text-3xl text-[var(--gold)] font-semibold">{d.badge.value}</div>
                  <div className="eyebrow mt-1">{d.badge.label}</div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee />

      {/* MANIFESTO */}
      <section className="bg-[var(--cream)] py-24 md:py-32">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8 grid lg:grid-cols-[0.8fr_1.2fr] gap-14 lg:gap-20">
          <div className="lg:sticky lg:top-32 self-start">
            <SectionHead eyebrow={d.manifesto.eyebrow} title={d.manifesto.title} intro={d.manifesto.intro} />
          </div>
          <div>
            {d.manifesto.items.map((it, i) => (
              <NumberedRow key={it.num} {...it} delay={i * 80} />
            ))}
            <div className="border-t border-black/10" />
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="grain bg-[#16233f] text-white py-24 md:py-32">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <Reveal>
            <div className="eyebrow mb-4">{d.whatWeDo.eyebrow}</div>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="font-serif text-[38px] md:text-[56px] font-semibold mb-16">
              {d.whatWeDo.title}
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-8">
            {d.whatWeDo.cards.map((c, i) => (
              <Reveal key={c.title} delay={i * 120}>
                <Link to={c.path} className="group block relative img-zoom rounded-sm overflow-hidden h-[420px]">
                  <img src={c.image} alt={c.title} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0b1120]/95 via-[#0b1120]/45 to-[#0b1120]/10" />
                  <div className="absolute bottom-0 left-0 p-8">
                    <div className="eyebrow mb-3">{c.chapter}</div>
                    <h3 className="font-serif text-3xl md:text-4xl font-semibold mb-3">{c.title}</h3>
                    <p className="text-white/70 max-w-md mb-5 leading-relaxed">{c.body}</p>
                    <span className="arrow-link text-[var(--gold)]">
                      Explore <ArrowRight size={16} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="bg-[var(--cream)] py-24 md:py-32">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <Reveal>
            <div className="eyebrow mb-8">{d.founder.eyebrow}</div>
          </Reveal>
          <Reveal delay={80}>
            <blockquote className="font-serif text-[34px] md:text-[54px] leading-[1.12] font-semibold text-[#14161c] max-w-5xl">
              {d.founder.quote.map((p, i) =>
                p.accent ? (
                  <span key={i} className="italic text-[var(--gold)]">{p.accent}</span>
                ) : (
                  <span key={i}>{p.plain}</span>
                )
              )}
            </blockquote>
          </Reveal>
          <Reveal delay={140}>
            <p className="text-[#55585f] leading-relaxed max-w-2xl mt-8">{d.founder.body}</p>
          </Reveal>
          <Reveal delay={200}>
            <Link to={d.founder.cta.path} className="arrow-link text-[#14161c] mt-8">
              {d.founder.cta.label} <ArrowRight size={16} />
            </Link>
          </Reveal>

          <div className="grid sm:grid-cols-3 gap-8 mt-20 pt-14 border-t border-black/10">
            {d.founder.stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 90}>
                <div>
                  <div className="font-serif text-[44px] md:text-[56px] text-[var(--gold)] font-semibold leading-none">
                    {s.value}
                  </div>
                  <div className="text-[#55585f] mt-3">{s.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <ClientsSection />

      <CTABand title={d.cta.title} button={d.cta.button} />
    </div>
  );
};

export default Home;
